import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import App from "./App";
import { PersonalWorkspaceProvider } from "./features/time-tracking/PersonalWorkspace";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { OrganizationProvider } from "./contexts/OrganizationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { UnsavedChangesProvider } from "./contexts/UnsavedChangesContext";
import { AgencySessionProvider } from "./features/agency/AgencySessionContext";
import { createQueryClient } from "./lib/query-client";
import {
  clearCustomHostnameAuthStorage,
  getCanonicalRelayRedirectUrl,
  isCanonicalRelayHostname,
} from "./lib/hostname-guard";
import "./index.css";

// Self-heal stale tabs after a deploy. Vite emits `vite:preloadError` when a
// dynamic-import chunk 404s — typically a tab held open across a deploy whose
// old asset hashes no longer exist. One forced reload pulls the fresh
// index.html (and its current chunk graph), then the tab keeps going. The
// sessionStorage flag breaks the loop if reload-then-still-erroring keeps
// happening (real CDN/build problem, not skew) so we surface the error
// instead of reloading forever.
const CHUNK_RELOAD_FLAG = "relay:chunk-reloaded";

window.addEventListener("vite:preloadError", (event) => {
  if (sessionStorage.getItem(CHUNK_RELOAD_FLAG) === "1") {
    return;
  }
  event.preventDefault();
  sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
  window.location.reload();
});

setTimeout(() => {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
}, 5000);

// One shared QueryClient, with global query/mutation error telemetry to
// relay.client_events. Built in lib/query-client.ts so a test can construct it;
// this module renders the app at import time.
const queryClient = createQueryClient();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing #root in index.html");
}

if (!isCanonicalRelayHostname(window.location.hostname)) {
  for (const cookie of clearCustomHostnameAuthStorage(localStorage, document.cookie)) {
    document.cookie = cookie;
  }
}

const canonicalRedirectUrl = getCanonicalRelayRedirectUrl(window.location);
if (canonicalRedirectUrl) {
  window.location.replace(canonicalRedirectUrl);
} else {
  // A data router (rather than <BrowserRouter>) is required so
  // UnsavedChangesProvider can call useBlocker to intercept browser
  // Back/Forward navigation while a dashboard has unsaved edits. The
  // existing ~100+ routes defined in App's internal <Routes> are left
  // untouched by mounting the provider-wrapped App at a single catch-all
  // "*" route — the splat route's pathnameBase is "/", so App's <Routes>
  // still matches the full URL exactly as it did under <BrowserRouter>.
  const router = createBrowserRouter(
    [
      {
        path: "*",
        element: (
          <UnsavedChangesProvider>
            <PersonalWorkspaceProvider>
              <App />
            </PersonalWorkspaceProvider>
          </UnsavedChangesProvider>
        ),
      },
    ],
    { future: { v7_relativeSplatPath: true } },
  );

  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <AgencySessionProvider>
                {/* OrganizationProvider depends on AuthProvider's session and
                    consumes AgencySessionProvider to expose the operated client
                    org while an agency support session is active. Neither this
                    provider chain nor Auth/Agency/Branding below reads router
                    context, so they can safely sit outside RouterProvider. */}
                <OrganizationProvider>
                  <BrandingProvider>
                    <RouterProvider router={router} />
                  </BrandingProvider>
                </OrganizationProvider>
              </AgencySessionProvider>
            </AuthProvider>
          </QueryClientProvider>
          {/* App-wide toast outlet (sonner). Restored after a dev merge dropped
              the render while keeping the import — every toast() call across the
              app is invisible without this. Inside ThemeProvider so it inherits
              light/dark; outside the router since toasts are app-global. */}
          <Toaster richColors closeButton position="top-right" />
        </ThemeProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
